/* eslint-disable max-lines-per-function */
/* eslint-disable max-depth */
/* eslint-disable complexity */
import settings from 'Cloud/settings';
import { UserFullQuotaAPICall } from 'reactApp/api/user/FullQuotaAPICall';
import { IS_AUTO_TEST_HIDE, IS_BLOCKED, IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { overquotaAfterClear } from 'reactApp/appHelpers/featuresHelpers/features/overquotaAfterClear';
import { overquotaNonPromoFlow } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNonPromoFlow';
import { overquotaDebugLogger } from 'reactApp/appHelpers/featuresHelpers/features/overquotaPre';
import { OverquotaUseApiStatus } from 'reactApp/appHelpers/featuresHelpers/features/overquotaUseApiStatus';
import { isPublicPasswordLinkEnabled, isThemedPublicLinkEnabled } from 'reactApp/appHelpers/featuresHelpers/features/promoLinks';
import { subscriptionsForDeletedUsers } from 'reactApp/appHelpers/featuresHelpers/features/subscriptionsForDeletedUsers';
import { tempHideOverquotaBanner } from 'reactApp/appHelpers/featuresHelpers/features/tempHideOverquotaBanner';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { isNewOverquotaModal } from 'reactApp/modules/features/features.helpers';
import { productsController } from 'reactApp/modules/products/products.controller';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { EProductPeriod } from 'reactApp/modules/products/products.types';
import { sendOverquotaInfo } from 'reactApp/modules/profile/profile.helpers';
import { setProfileOverquotaStateSuccess } from 'reactApp/modules/profile/profile.module';
import { handleLoadUserFullProfile } from 'reactApp/modules/profile/profile.saga';
import { getUserOverquotaState } from 'reactApp/modules/profile/profile.selectors';
import type { ProfileOverquota } from 'reactApp/modules/profile/profile.types';
import { getCurrentStorage, isReactLandingPage } from 'reactApp/modules/router/router.selectors';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { trashbinUpdateSizeSuccess } from 'reactApp/modules/trashbin/trashbin.module';
import { getTrashbinSize, isTrashSizeLoaded, isTrashSizeLoading } from 'reactApp/modules/trashbin/trashbin.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserStorageActions } from 'reactApp/modules/user/userStorage';
import { loadUserQuotaSuccess } from 'reactApp/modules/userQuota/userQuota.module';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { ViewerSelectors } from 'reactApp/modules/viewer/viewer.selectors';
import { renderMobileSplashScreen } from 'reactApp/ui/Mobile/SplashScreen/helpers/SplashScreen.helpers';
import { useOverquotaSplashScreen } from 'reactApp/ui/Mobile/SplashScreen/hooks/useOverquotaSplashScreen';
import { renderOverquotaModal } from 'reactApp/ui/OverquotaPopup/new/OverquotaModal.helpers';
import { renderOverquotaSplash } from 'reactApp/ui/OverquotaPopup/newSplash/OverquotaSplash.helpers';
import { type ConditionsWithData, checkConditions, ConditionsCheckType } from 'reactApp/utils/conditionChecker';
import { sendKaktamLog, sendXray } from 'reactApp/utils/ga';
import { captureException } from 'reactApp/utils/tracer';
import { delay, put, race } from 'redux-saga/effects';
import { ON_PREMISE } from 'server/constants/environment';
import type { QuotaStates } from 'server/types/context/IUser';
import { call, select, take } from 'typed-redux-saga';

const QUOTA_TIP_ID = 'overQuotaTip';

const WAIT_QUOTA_API_TIMEOUT = 5000;

const statesForSplash: QuotaStates[] = ['blocked', 'cleared', 'after_clear'];

const statesMobileAsDesktop = ['blocked'];

const sendShowQuotaBannerForNonOverQuotaUser = (text: string) => sendXray(['overqbanner', 'show-nonquota', text || 'none']);

function* checkStopOverquotaProcessing({
    state,
    stateCleared,
    isPhone,
}: {
    state: QuotaStates | undefined;
    stateCleared: boolean;
    isPhone: boolean;
}) {
    const { result: result1, resolvedConditions: resolverConditions1 } = checkConditions(
        {
            afterClearDisabled: {
                check: state === 'after_clear' && !overquotaAfterClear,
                state,
                overquotaAfterClear,
            },
        } as ConditionsWithData,
        ConditionsCheckType.FirstTrue
    );

    overquotaDebugLogger.switch(result1, 'checkStopOverquotaProcessing step 1', resolverConditions1);

    if (state === 'after_clear' && !overquotaAfterClear) {
        return true;
    }

    const { action = null } = yield* select(SettingsSelectors.getQueryParams);
    const storage = yield* select(getCurrentStorage);
    const { isHome, isPrivatePage, isAllDocuments } = getStorage(storage);
    const isWebview = yield* select(EnvironmentSelectors.isWebview);
    const isLP = isReactLandingPage();

    const { result: result2, resolvedConditions: resolverConditions2 } = checkConditions(
        {
            requestPaymentAction: { check: !stateCleared && action === 'request-payment', state, action },
            publicPasswordLinkAction: { check: !stateCleared && isPublicPasswordLinkEnabled, state, action },
            themedPublicLinkEnabled: { check: !stateCleared && isThemedPublicLinkEnabled, state, action },
            webview: { check: !stateCleared && isPhone && isWebview, state, isPhone, isWebview },
            settingsRequestAction: {
                check: !stateCleared && isPhone && settings?.request?.action,
                isPhone,
                state,
                action: settings?.request?.action,
            },
            mobileLandingOrSubsOrFamily: {
                check: !stateCleared && isPhone && (isLP || storage === EStorageType.subscriptions || storage === EStorageType.family),
                state,
                isPhone,
                storage,
                isLP,
            },
        } as ConditionsWithData,
        ConditionsCheckType.FirstTrue
    );

    overquotaDebugLogger.switch(result2, 'checkStopOverquotaProcessing step 2', resolverConditions2);

    if (!stateCleared && (action === 'request-payment' || isPublicPasswordLinkEnabled || isThemedPublicLinkEnabled)) {
        return true;
    }

    if (
        !stateCleared &&
        isPhone &&
        (isWebview || settings?.request?.action || isLP || storage === EStorageType.subscriptions || storage === EStorageType.family)
    ) {
        return true;
    }

    // Форс-включение промки для отдельных автотестов.
    const force = yield put(UserStorageActions.get(`force_${QUOTA_TIP_ID}`)) || false;

    const showOverquotaInPrivatePages = isHome || (isPrivatePage && state === 'full_blocked' && !isAllDocuments);

    const { result: result3, resolvedConditions: resolverConditions3 } = checkConditions(
        {
            notShowInPrivatePages: {
                check: !stateCleared && !isPhone && !showOverquotaInPrivatePages,
                state,
                isPhone,
                showOverquotaInPrivatePages: {
                    storage,
                    isPrivatePage,
                },
            },
            autoTestHide: {
                check: !stateCleared && !isPhone && IS_AUTO_TEST_HIDE && !force,
                state,
                isPhone,
                IS_AUTO_TEST_HIDE,
                force,
            },
        } as ConditionsWithData,
        ConditionsCheckType.FirstTrue
    );

    overquotaDebugLogger.switch(result3, 'checkStopOverquotaProcessing step 3', resolverConditions3);

    if (!stateCleared && !isPhone && (!showOverquotaInPrivatePages || (IS_AUTO_TEST_HIDE && !force))) {
        return true;
    }

    const isViewerActive = yield* select(ViewerSelectors.isViewerActive);

    const { result: result4, resolvedConditions: resolverConditions4 } = checkConditions(
        {
            isViewerActive,
        } as ConditionsWithData,
        ConditionsCheckType.FirstTrue
    );

    overquotaDebugLogger.switch(result4, 'checkStopOverquotaProcessing step 4', resolverConditions4);

    /* eslint-disable sonarjs/prefer-single-boolean-return */
    if (isViewerActive) {
        return true;
    }

    return false;
}

function* tryRenderPopupsForOverquoters({
    stateCleared,
    isPhone,
    quotaState,
    isOverQuotaMailAndCloud,
    isOverquotaFromProfileApi,
}: {
    quotaState: ProfileOverquota | undefined;
    stateCleared: boolean;
    isPhone: boolean;
    isOverQuotaMailAndCloud: boolean;
    isOverquotaFromProfileApi: boolean;
}) {
    const { over } = yield* select(UserSelectors.getCloudSpace);
    const state = quotaState?.state;

    /** https://jira.vk.team/browse/CLOUDWEB-18411
     * Вынес в отдельный флоу чтобы исключить срабатывание ложных промок на мобилке, но гарантированно показать */
    if (stateCleared) {
        if (isNewOverquotaModal) {
            yield* call(renderOverquotaSplash, {
                mode: !overquotaAfterClear && state === 'after_clear' ? 'cleared' : state,
            });

            if (!isOverQuotaMailAndCloud) {
                sendShowQuotaBannerForNonOverQuotaUser(`fixed_${state}`);
            }
        }
    } else if (isPhone && !(state && statesMobileAsDesktop.includes(state))) {
        yield productsController.loadProducts();
        const { product } = yield* select(ProductsSelectors.getProductAndTariffByQuota, over.original, EProductPeriod.year, false);

        yield* call(renderMobileSplashScreen, {
            contentHook: useOverquotaSplashScreen,
            hookParams: { productId: product?.id || '' },
        });
    } else {
        if (isNewOverquotaModal) {
            try {
                if (state && over.original > 0) {
                    let isSizeLoaded = yield* select(isTrashSizeLoaded);
                    const isSizeLoading = yield* select(isTrashSizeLoading);
                    let trashSize = yield* select(getTrashbinSize);

                    if (isSizeLoading && !isSizeLoaded) {
                        yield take(trashbinUpdateSizeSuccess);
                        isSizeLoaded = true;
                        trashSize = yield* select(getTrashbinSize);
                    }

                    if (!isSizeLoaded) {
                        const { data } = yield new UserFullQuotaAPICall().makeRequest({ trash: true });

                        if (data.status === 200) {
                            trashSize = data.body.trash_size || 0;
                        }
                    }

                    if (over.original < trashSize) {
                        yield put(setProfileOverquotaStateSuccess({ trashSize }));
                    }
                }
            } catch (_) {}

            if (state && statesForSplash.includes(state)) {
                yield* call(renderOverquotaSplash, {
                    mode: !overquotaAfterClear && state === 'after_clear' ? 'cleared' : state,
                });

                if (!isOverQuotaMailAndCloud) {
                    sendShowQuotaBannerForNonOverQuotaUser(`splash_${state}`);
                }
            } else if (isOverquotaFromProfileApi && !tempHideOverquotaBanner) {
                yield* call(renderOverquotaModal);

                if (!isOverQuotaMailAndCloud) {
                    sendShowQuotaBannerForNonOverQuotaUser(`modal_${state}`);
                }

                sendKaktamLog(
                    {
                        text: 'show overquota modal-2',
                        state,
                        quotaState,
                        isOverQuota: isOverQuotaMailAndCloud,
                        isOverquotaFromProfileApi,
                        time: Date.now().toLocaleString(),
                    },
                    'overquotacloud'
                );
            }

            return;
        }

        if (!isOverQuotaMailAndCloud) {
            sendShowQuotaBannerForNonOverQuotaUser(`others_${state}`);
        }
    }
}

export function* initOverquotaProcessing() {
    if (!overquotaNonPromoFlow) {
        return;
    }

    const isAnonymous = yield* select(UserSelectors.isAnonymous);
    const isBizUser = yield* select(UserSelectors.isBizUser);
    const isCorp = yield* select(UserSelectors.isCorpUser);
    const isPhone = yield* select(EnvironmentSelectors.isPhone);

    const overquotaDisableConditions = {
        isAnonymous,
        isBizUser,
        isCorp,
        ON_PREMISE,
        isDesktopModalDisabled: { check: !isNewOverquotaModal && !isPhone, isPhone, isNewOverquotaModal },
        isBlockedUserDisabled: { check: IS_BLOCKED && !subscriptionsForDeletedUsers, IS_BLOCKED, subscriptionsForDeletedUsers },
    };

    const { result: overquotaDisable, resolvedConditions: overquotaDisableConditionsResolved } = checkConditions(
        overquotaDisableConditions,
        ConditionsCheckType.FirstTrue
    );

    overquotaDebugLogger.switch(overquotaDisable, 'initOverquotaProcessing', overquotaDisableConditionsResolved);

    if (
        isAnonymous ||
        isBizUser ||
        isCorp ||
        ON_PREMISE ||
        !(isNewOverquotaModal || isPhone) ||
        (IS_BLOCKED && !subscriptionsForDeletedUsers)
    ) {
        return;
    }

    try {
        yield* handleLoadUserFullProfile(false, true);

        const quotaState = yield* select(getUserOverquotaState);
        const state = quotaState?.state;
        const isOverQuotaCloud = yield* select(UserSelectors.isOverQuotaUser);
        const isOverQuotaMailAndCloud = yield* select(UserQuotaSelectors.isOverquota);

        if (quotaState?.status === 'failed') {
            sendXray(['overquota', 'statenew', 'failedapi', `${isOverQuotaMailAndCloud ? 'is' : 'non'}over`]);
            sendKaktamLog(
                {
                    text: 'failed state api call',
                    state,
                    quotaState,
                    isOverQuotaCloud,
                    isOverQuotaMailAndCloud,
                    time: Date.now().toLocaleString(),
                },
                'overquotacloud'
            );
        }

        const stateCleared = (state === 'cleared' || state === 'after_clear') && subscriptionsForDeletedUsers;
        const isQuotaLoaded = yield* select(UserQuotaSelectors.isQuotaLoaded);

        if (!stateCleared && !isQuotaLoaded) {
            yield race({
                success: take([loadUserQuotaSuccess.toString()]),
                timeout: delay(WAIT_QUOTA_API_TIMEOUT),
            });
        }
        const isOverquotaFromProfileApi = yield* select(UserQuotaSelectors.isOverquotaBySpaceAndApi);
        const stopProcessing = yield checkStopOverquotaProcessing({ state, stateCleared, isPhone });

        sendOverquotaInfo({
            overquotaStatus: quotaState?.status === 'failed' ? 'failedapi' : state,
            overquotaResolve: OverquotaUseApiStatus
                ? isOverquotaFromProfileApi
                : isOverQuotaMailAndCloud || isOverQuotaCloud || isOverquotaFromProfileApi,
            stopProcessing,
        });

        if (stopProcessing) {
            return;
        }

        /**
         * Пользователь зажимается если квота превышена и правильный статус с бэкенда
         * Иначе завершаем обработку, чтобы не показывать оверквотные коммуникации
         */
        if (OverquotaUseApiStatus) {
            if (!isOverquotaFromProfileApi) {
                return;
            }
        } else if (!(isOverQuotaMailAndCloud || isOverQuotaCloud) && !isOverquotaFromProfileApi) {
            return;
        }

        if (!state) {
            const space = yield* select(UserSelectors.getCloudSpace);
            const isNewbie = yield* select(UserSelectors.isNewbie);

            if (space?.usedBytes < 1 && isOverQuotaMailAndCloud) {
                // Чисто почтовые оверквотеры без занятого места в облаке
                sendXray(['overquota', 'mailonly', 'nocloud']);
                return;
            }

            sendKaktamLog(
                {
                    text: 'overquota-state-none',
                    isOverQuotaCloud,
                    isOverQuotaMailAndCloud,
                    isOverquotaFromProfileApi,
                    status: quotaState?.status,
                    space,
                    isNewbie,
                    isPhone: IS_PHONE_BROWSER,
                    time: Date.now().toLocaleString(),
                },
                'overquotacloud'
            );
            sendXray(['overquota', 'nostate', isNewbie ? 'new' : 'nonnew', quotaState?.status || 'nostatus']);
        }

        yield tryRenderPopupsForOverquoters({ stateCleared, isPhone, quotaState, isOverQuotaMailAndCloud, isOverquotaFromProfileApi });
    } catch (error: any) {
        captureException(error, { issueKey: 'overquota/init' });
    }
}
